import React from 'react';
import { ApolloError } from '@apollo/client';
import { getApplicationError, getValidationErrors } from '@oysterjs/core/graphql/errors';

import { client } from '~/api/client';
import {
  type CaraWorkspaceFieldsFragment,
  UpdateCaraWorkspacesDocument,
  type UpdateCaraWorkspacesMutation
} from '~/api/graphql';

/**
 * Owns the "rename workspace" mutation. The optimistic title is applied
 * immediately to the local workspace so the input doesn't flicker back to the
 * old value while the request is in flight.
 *
 * Empty / whitespace-only titles are rejected (the system seeds an initial
 * title asynchronously, and accepting empty here would either be overwritten
 * server-side or set a useless title).
 */
export function useRenameCaraWorkspace(args: {
  workspace: CaraWorkspaceFieldsFragment | undefined;
  setWorkspace: (workspace: CaraWorkspaceFieldsFragment) => void;
}) {
  const { workspace, setWorkspace } = args;
  const [renameLoading, setRenameLoading] = React.useState(false);
  const [renameError, setRenameError] = React.useState<string>();

  const renameCaraWorkspace = React.useCallback(
    async (nextTitle: string): Promise<boolean> => {
      if (!workspace || renameLoading) return false;

      const trimmed = nextTitle.trim();
      if (!trimmed) return false;
      if (trimmed === workspace.title) return false;

      setRenameLoading(true);
      setRenameError(undefined);

      // Apply optimistic title locally so the input stays stable while the
      // mutation resolves. Reverted on error.
      const previous = workspace;
      setWorkspace({ ...workspace, title: trimmed });

      try {
        const res = await client.mutate<UpdateCaraWorkspacesMutation>({
          mutation: UpdateCaraWorkspacesDocument,
          variables: { workspaceIds: [workspace.id], input: { title: trimmed } }
        });

        const applicationError = getApplicationError(
          new ApolloError({ graphQLErrors: res.errors })
        );
        const validationErrors = getValidationErrors(
          new ApolloError({ graphQLErrors: res.errors })
        );

        if (applicationError) {
          setRenameError(applicationError.message);
          setWorkspace(previous);
          return false;
        }
        if (validationErrors?.length) {
          setRenameError(validationErrors[0].message);
          setWorkspace(previous);
          return false;
        }

        const updated = res.data?.updateCaraWorkspaces?.find((w) => w.id === workspace.id);
        if (updated) {
          setWorkspace(updated);
        }
        return true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        setWorkspace(previous);
        setRenameError('Failed to rename workspace');
        return false;
      } finally {
        setRenameLoading(false);
      }
    },
    [workspace, renameLoading, setWorkspace]
  );

  return { renameLoading, renameError, renameCaraWorkspace };
}
